import { useCallback } from "react"

export const STORAGE_KEYS = {
  HIDDEN_LAYER_OPACITY: "pcb_viewer_hidden_layer_opacity",
  IS_SHOWING_PCB_GROUPS: "pcb_viewer_is_showing_pcb_groups",
  PCB_GROUP_VIEW_MODE: "pcb_viewer_group_view_mode",
  IS_SHOWING_COPPER_POURS: "pcb_viewer_is_showing_copper_pours",
  IS_SHOWING_COURTYARDS: "pcb_viewer_is_showing_courtyards",
  IS_SHOWING_GROUP_ANCHOR_OFFSETS: "pcb_viewer_is_showing_group_anchor_offsets",
  IS_SHOWING_SOLDER_MASK: "pcb_viewer_is_showing_solder_mask",
  IS_SHOWING_FABRICATION_NOTES: "pcb_viewer_is_showing_fabrication_notes",
  IS_SHOWING_PCB_NOTES: "pcb_viewer_is_showing_pcb_notes",
  IS_SHOWING_SILKSCREEN: "pcb_viewer_is_showing_silkscreen",
} as const

export const getStoredBoolean = (
  key: string,
  defaultValue: boolean,
): boolean => {
  if (typeof window === "undefined") return defaultValue
  try {
    const stored = localStorage.getItem(key)
    return stored !== null ? JSON.parse(stored) : defaultValue
  } catch {
    return defaultValue
  }
}

export const setStoredBoolean = (key: string, value: boolean): void => {
  if (typeof window === "undefined") return
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {}
}

export const getStoredString = (key: string, defaultValue: string): string => {
  if (typeof window === "undefined") return defaultValue
  try {
    const stored = localStorage.getItem(key)
    return stored !== null ? JSON.parse(stored) : defaultValue
  } catch {
    return defaultValue
  }
}

export const setStoredString = (key: string, value: string): void => {
  if (typeof window === "undefined") return
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {}
}

export const getStoredNumber = (key: string, defaultValue: number): number => {
  if (typeof window === "undefined") return defaultValue
  try {
    const stored = localStorage.getItem(key)
    const value = stored !== null ? JSON.parse(stored) : null
    return typeof value === "number" && Number.isFinite(value)
      ? value
      : defaultValue
  } catch {
    return defaultValue
  }
}

export const setStoredNumber = (key: string, value: number): void => {
  if (typeof window === "undefined") return
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {}
}

export const useLocalStorage = () => {
  const getBoolean = useCallback(
    (key: string, defaultValue: boolean): boolean => {
      return getStoredBoolean(key, defaultValue)
    },
    [],
  )

  const setBoolean = useCallback((key: string, value: boolean): void => {
    setStoredBoolean(key, value)
  }, [])

  return {
    getBoolean,
    setBoolean,
  }
}

export const useLocalStorageValue = (key: string, defaultValue: boolean) => {
  const { getBoolean, setBoolean } = useLocalStorage()

  const getValue = useCallback(() => {
    return getBoolean(key, defaultValue)
  }, [getBoolean, key, defaultValue])

  const setValue = useCallback(
    (value: boolean) => {
      setBoolean(key, value)
    },
    [setBoolean, key],
  )

  return {
    getValue,
    setValue,
  }
}
